import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Search, Settings2, Mail, Briefcase } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { listEmployees } from '@/services/api/collaborators';
import type { Employee, MetaPage } from '../types/meta.types';
import CollaboratorAccessEditor from './CollaboratorAccessEditor';

interface CollaboratorsListProps {
    pages: MetaPage[];
}

export default function CollaboratorsList({ pages }: CollaboratorsListProps) {
    const [searchTerm, setSearchTerm] = useState('');
    const [editing, setEditing] = useState<Employee | null>(null);

    const { data: employees = [], isLoading, error } = useQuery({
        queryKey: ['employees'],
        queryFn: listEmployees,
        staleTime: 1000 * 60 * 5, // 5min — lista raramente muda
    });

    const filtered = useMemo(() => {
        if (!searchTerm) return employees;
        const q = searchTerm.toLowerCase();
        return employees.filter(e =>
            e.name.toLowerCase().includes(q) || e.email.toLowerCase().includes(q)
        );
    }, [employees, searchTerm]);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-900">Colaboradores</h1>
                <p className="mt-2 text-gray-600 max-w-2xl">
                    Funcionários que você cadastrou no apisistema. Defina o que cada um
                    pode acessar aqui no chatmeta — por conta Meta (todas as páginas dela)
                    ou por página específica.
                </p>
            </div>

            {/* Busca */}
            {employees.length > 0 && (
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            placeholder="Buscar por nome ou email..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            )}

            {/* Lista */}
            {isLoading ? (
                <div className="bg-white rounded-lg p-12 text-center shadow border border-gray-100">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto" />
                    <p className="text-sm text-gray-500 mt-3">Carregando colaboradores...</p>
                </div>
            ) : error ? (
                <div className="bg-white rounded-lg p-12 text-center shadow border border-gray-100">
                    <p className="text-red-600">Erro ao carregar colaboradores.</p>
                </div>
            ) : employees.length === 0 ? (
                <div className="bg-white rounded-lg p-16 text-center shadow border border-gray-100">
                    <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
                        <Users className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Nenhum colaborador</h3>
                    <p className="text-gray-500 max-w-sm mx-auto">
                        Convide funcionários pelo apisistema e eles aparecerão aqui pra você
                        configurar os acessos.
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Colaborador</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Projetos</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filtered.map((e) => (
                                <tr key={e.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-full bg-indigo-50 flex items-center justify-center border border-indigo-100">
                                                <span className="text-sm font-semibold text-indigo-700">
                                                    {e.name.substring(0, 2).toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-gray-900">{e.name}</p>
                                                <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                                    <Mail className="h-3 w-3" />{e.email}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-wrap gap-1 max-w-md">
                                            {e.projects.length === 0 ? (
                                                <span className="text-xs text-gray-400">—</span>
                                            ) : e.projects.slice(0, 3).map(p => (
                                                <Badge
                                                    key={p.project_id}
                                                    variant="outline"
                                                    className="text-[10px] py-0 h-5 border-gray-200 bg-gray-50/50 text-gray-700"
                                                    title={`role: ${p.project_role}`}
                                                >
                                                    <Briefcase className="h-2.5 w-2.5 mr-1" />
                                                    {p.project_name}
                                                </Badge>
                                            ))}
                                            {e.projects.length > 3 && (
                                                <Badge variant="outline" className="text-[10px] py-0 h-5">
                                                    +{e.projects.length - 3}
                                                </Badge>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {e.is_active ? (
                                            <Badge variant="success">Ativo</Badge>
                                        ) : (
                                            <Badge variant="destructive">Inativo</Badge>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setEditing(e)}
                                            className="text-indigo-600 hover:bg-indigo-50 h-9 px-3"
                                        >
                                            <Settings2 className="h-4 w-4 mr-1.5" />
                                            Gerenciar acessos
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <CollaboratorAccessEditor
                open={!!editing}
                onOpenChange={(open) => { if (!open) setEditing(null); }}
                collaborator={editing}
                pages={pages}
            />
        </div>
    );
}
